import type { PayloadAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../store';
import { DefaultCalendarSetSoloOrario } from '../pages/Default/store';
import { PersonalCalendarSetSoloOrario } from '../pages/Personal/store';
import { StudioCalendarSetSoloOrario } from '../pages/Studio/store';
import { UserCalendarSetSoloOrario } from '../pages/User/store';
import type { CalendarioId } from '../utility_objects/preferenzeCalendario';

// Un selettore per calendario, creato una volta sola: identità stabile per
// useSelector
const SELETTORI: Record<CalendarioId, (state: RootState) => boolean> = {
  negozio: (state) => state.bluBookingDefaultCalendar.soloOrario,
  personale: (state) => state.bluBookingPersonalCalendar.soloOrario,
  studio: (state) => state.bluBookingStudioCalendar.soloOrario,
  utente: (state) => state.bluBookingUserCalendar.soloOrario,
};

const AZIONI: Record<
  CalendarioId,
  (acceso: boolean) => PayloadAction<boolean>
> = {
  negozio: DefaultCalendarSetSoloOrario,
  personale: PersonalCalendarSetSoloOrario,
  studio: StudioCalendarSetSoloOrario,
  utente: UserCalendarSetSoloOrario,
};

/**
 * Selettore della funzione «Solo orario» (Ore «Prenotabili») del calendario;
 * per chi deve solo leggerla (il wrapper del calendario, il pallino del
 * pulsante Visualizzazione).
 */
export const selectSoloOrario = (
  calendario: CalendarioId
): ((state: RootState) => boolean) => SELETTORI[calendario];

/**
 * Funzione «Solo orario», la riga Ore del riquadro Visualizzazione: con
 * «Prenotabili» le viste orarie nascondono le ore prima dell'apertura e dopo
 * la chiusura, con «Tutte» mostrano le ore di sempre. Scelta del calendario:
 * vive nello store del calendario ed è ricordata per PC nel suo cookie
 * (COOKIE_SOLO_ORARIO: defaultCalendarSoloOrario, personalCalendarSoloOrario,
 * studioCalendarSoloOrario, userCalendarSoloOrario; assente = «Tutte»).
 */
export const useSoloOrario = (
  calendario: CalendarioId
): {
  soloOrario: boolean;
  imposta: (acceso: boolean) => void;
} => {
  const dispatch = useDispatch<AppDispatch>();
  const soloOrario = useSelector(selectSoloOrario(calendario));
  const imposta = useCallback(
    (acceso: boolean) => dispatch(AZIONI[calendario](acceso)),
    [dispatch, calendario]
  );
  return { soloOrario, imposta };
};
